// Radiology compendium data — procedures organized by modality
// Source: Radiology Compendium.xlsx

export const RADIOLOGY_QUESTIONNAIRE_ID = 'radiology-order-requisition'

export interface RadiologyProcedure {
  code: string
  name: string
  modality: string
}

export interface RadiologyModality {
  key: string
  label: string
  procedures: RadiologyProcedure[]
}

export interface RadiologyFormState {
  selectedProcedures: RadiologyProcedure[]
  selectedFacilityId: string
  diagnosisText: string
  clinicalNotes: string
  relevantHistory: string
  isPregnant: 'yes' | 'no' | 'na' | ''
  contrastAllergy: boolean
  priorAuthCode: string
  providerSignatureDataUrl: string
  providerSignatureDate: string
}

export function createInitialRadiologyFormState(): RadiologyFormState {
  return {
    selectedProcedures: [],
    selectedFacilityId: '',
    diagnosisText: '',
    clinicalNotes: '',
    relevantHistory: '',
    isPregnant: '',
    contrastAllergy: false,
    priorAuthCode: '',
    providerSignatureDataUrl: '',
    providerSignatureDate: '',
  }
}

/** A study name, or a `[name, code]` pair pinning a code that auto-numbering must not assign. */
type ProcedureEntry = string | readonly [name: string, code: string]

/**
 * Auto-numbers each study from its position in the list.
 *
 * PD-1642 — create-order records these codes verbatim as the RequestGroup action
 * display, so renumbering repoints the codes on orders that are already submitted.
 * A study added later therefore carries an explicit `[name, code]` pair: it can then
 * sit in alphabetical order without shifting the auto-numbered entries below it,
 * which keep counting as though the pinned entries were not there.
 */
function procs(modality: string, entries: readonly ProcedureEntry[]): RadiologyProcedure[] {
  const prefix = modality
    .replace(/[^A-Za-z]/g, '')
    .slice(0, 4)
    .toUpperCase()
  let autoNumbered = 0
  return entries.map((entry) => {
    if (typeof entry !== 'string') {
      return { code: entry[1], name: entry[0], modality }
    }
    autoNumbered += 1
    return {
      code: `${prefix}-${String(autoNumbered).padStart(3, '0')}`,
      name: entry,
      modality,
    }
  })
}

export const RADIOLOGY_COMPENDIUM: RadiologyModality[] = [
  {
    key: 'ct_angiography',
    label: 'CT Angiography',
    procedures: procs('CT Angiography', [
      'Abdomen',
      'Abdomen & Pelvis',
      'Abdomen Aorta',
      'Abdomen Visceral Arteries',
      'Aortoiliac Runoff',
      'Chest',
      'Dissection Protocol',
      'Elbow',
      'Extremity Runoff',
      'Head/Brain',
      'Heart',
      'Intracranial',
      'Lower Extremity',
      'Neck',
      'Other',
      'Pelvis',
      'Pulmonary',
      'Renal',
      'Renal Arteries',
      'Thoracic Aorta',
      'Upper Extremity',
      'Wrist',
    ]),
  },
  {
    key: 'ct_scan',
    label: 'CT Scan',
    procedures: procs('CT Scan', [
      'Abdomen',
      'Abdomen Triphasic (Liver Protocol)',
      'Ankle',
      'Appendix Protocol',
      'Arthrogram Joint',
      'Brachial Plexus',
      'Cardiac Calcium Score',
      'Cervical Spine',
      'Chest',
      'Clavicle',
      'Dental Scan',
      'Elbow',
      'Facial Bones',
      'Femur',
      'Finger',
      'Foot',
      'Forearm',
      'Hand',
      'Head/Brain',
      'Heart',
      'Hematuria Protocol',
      'Hip',
      'Inner Ear',
      'Kidney Stone Protocol',
      'Knee',
      'Lower Arm',
      'Lumbar Spine',
      'Mandible',
      'Maxillofacial',
      'Multiphasic Adrenal Protocol',
      'Multiphasic Liver Protocol',
      'Multiphasic Pancreatic Protocol',
      'Neck',
      'Orbit/IAC',
      'Other',
      'Pancreatic Mass',
      'Pancreatic Protocol',
      'Pelvis',
      'Pulmonary Embolism Study (PE)',
      'Renal',
      'Renal Mass Protocol',
      'Shoulder',
      'Spine Post Myelogram',
      'Temporal Bones',
      'Thoracic Spine',
      'Tib Fib',
      'Tibia-Fibula',
      'Urogram',
      'Virtual Colonography',
      'Wrist',
    ]),
  },
  {
    key: 'cardiac_exams',
    label: 'Cardiac Exams',
    procedures: procs('Cardiac Exams', [
      'Bubble Study',
      'ECG Tracing',
      'Echo Colorflow',
      'Echo Doppler',
      'Echocardiograms',
      'EKG',
      'Holter Monitoring',
      'PICC Placement',
      'Stress Echocardiograms',
    ]),
  },
  {
    key: 'fluoroscopy',
    label: 'Fluoroscopy',
    procedures: procs('Fluoroscopy', [
      'Air Contrast Barium Enema (BE)',
      'Arthrogram',
      'Barium Enema',
      'Barium Swallow',
      'Epidurography',
      'Esophogram',
      'Hysterosalpingography (HSG)',
      'Intravenous Pyelogram (IVP)',
      'Oral Cholecystogram',
      'Other',
      'Sialogram',
      'Small Bowel Follow Through (SBFT)',
      'Upper Gastrointestinal Series (UGI)',
      'Voiding Cystourethrogram (VCUG)',
    ]),
  },
  {
    key: 'interventional_radiology',
    label: 'Interventional Radiology',
    procedures: procs('Interventional Radiology', [
      'Breast Cyst Aspiration',
      'Breast Fine Needle Aspiration',
      'Central Venous Access',
      'Dialysis Access/Management',
      'Drain Placement/Management',
      'Fallopian Tube Recanalization',
      'Fluid Removal',
      'Lumbar Puncture Diagnostic',
      'MRI Breast Biopsy',
      'Non-Healing Wound Treatment',
      'Other Interventional Radiology',
      'PICC Line Placement',
      'PICC Line Removal',
      'Portacath Placement',
      'Portacath Removal',
      'Vascular Access',
      'Vertebroplasty',
    ]),
  },
  {
    key: 'interventional_ultrasound',
    label: 'Interventional Ultrasound',
    procedures: procs('Interventional Ultrasound', [
      'Aspiration',
      'Breast Biopsy - Automated Vacuum Assisted',
      'Breast Needle Core Biopsy',
      'Image Guidance Biopsy',
      'Liver Biopsy',
      'Lymph Node Biopsy',
      'Needle Biopsy',
      'Needle Placement (Guided)',
      'Other Interventional Ultrasound',
      'Paracentesis (Abdomen)',
      'Renal Biopsy',
      'Thoracentesis (Chest)',
      'Thyroid Biopsy',
      'Thyroid Fine Needle Aspiration',
    ]),
  },
  {
    key: 'mr_angiography',
    label: 'MR Angiography',
    procedures: procs('MR Angiography', [
      'Abdomen',
      'Abdomen w/3D Recon',
      'Abdominal Aorta',
      'Arthrogram',
      'Brain w/3D Recon',
      'Carotid Artery',
      'Carotid w/3D Recon',
      'Cervical Spine',
      'Chest',
      'Circle of Willis',
      'Extremity w/3D Recon',
      'Head',
      'Lower Extremity',
      'Lumbar Spine',
      'MRCP',
      'Neck',
      'Other',
      'Pelvis',
      'Peripheral w/3D Recon',
      'Renal/Aorta',
      'Runoff',
      'Spinal Canal',
      'Thoracic Spine',
      'TRIGEMINAL NERVE',
      'Upper Extremity',
    ]),
  },
  {
    key: 'mri',
    label: 'MRI',
    procedures: procs('MRI', [
      'Abdomen',
      'Ankle',
      'Arm',
      'Arthrogram',
      'Bone Marrow',
      'Brachial Plexus',
      'Brain',
      'Breast',
      'Carotid',
      ['Cervical Spine', 'MRI-051'],
      'Chest',
      'Clavicle',
      'Elbow',
      'Enterography',
      'Extremity w/Runoff',
      'Face',
      'Finger',
      'Foot',
      'Forearm',
      'Hand',
      'Head/Brain',
      'Hip',
      'IAC',
      'Iliac',
      'Knee',
      'Lower Leg',
      'Lumbar Spine',
      'Lumbosacral Spine',
      'Neck Soft Tissue',
      'Neurogram',
      'Orbits, Face, Neck',
      'Other',
      'Pelvis',
      'Pelvis Attn: Prostate',
      'Pituitary',
      'Posterior Fossa',
      'Renal Vessels',
      'Sacrum',
      'Sella',
      'Shoulder',
      'Soft Tissue Back',
      'Spectroscopy',
      'Spine',
      'Temporomandibular Joints (TMJ) W/O Contrast',
      ['Thoracic Spine', 'MRI-052'],
      'Tib Fib',
      'Toe',
      'TRAPEZIUS',
      'Trigeminal Nerve',
      'Upper Arm',
      'Venogram',
      'Wrist',
    ]),
  },
  {
    key: 'nuclear_medicine',
    label: 'Nuclear Medicine',
    procedures: procs('Nuclear Medicine', [
      'I-123 Thyroid Scan',
      'Biliary Scan',
      'Bone Scan',
      'Bone Scan (w/X-ray if needed) SPECT',
      'Bone Scan (w/X-ray if needed) Three Phase',
      'Bone Scan (w/X-ray if needed) Whole Body',
      'Cardiac Ejection Fraction',
      'Cardiac Nuclear Stress Testing',
      'Cardiac Scan',
      'DaT Scan',
      'Gastric Empty',
      'Gastric Emptying Evaluation',
      'Hepatobiliary Scan (HIDA)',
      'Hepatobiliary/Disida/HIDA w/o CCK',
      'Hepatobiliary/Disida/HIDA with CCK',
      'Lexiscan/Cardiolite',
      'Liver SPECT',
      'Liver Spleen Scan',
      'Lung Scan',
      "Meckel's Scan",
      'Multigated Acquisition Scan (MUGA)',
      'Myocardial Perfusion Scan',
      'Myoview Heart Scan w/EF & WM',
      'Non-Nuclear Treadmill Only Stress',
      'Octreotide Scan',
      'Other',
      'Parathyroid Planar Imaging',
      'Radionuclide Voiding Cystourethrogram',
      'Renal Captopril',
      'Renal Lasix',
      'Renal Plasma Flow',
      'Schilling Test',
      'Sentinel Node Study (SLN)',
      'Thyroid Metastatic Scan',
      'Thyroid Uptake Scan',
      'Triple Renal Scan',
      'Wall Motion Resting E/F',
      'WB I-131 Thyroid Carcinoma Scan',
      'WBC Scan',
    ]),
  },
  {
    key: 'osteoporosis_detection',
    label: 'Osteoporosis Detection',
    procedures: procs('Osteoporosis Detection', [
      'DEXA Body Composition Scan',
      'DEXA Bone Density',
    ]),
  },
  {
    key: 'pet_scan',
    label: 'PET Scan',
    procedures: procs('PET Scan', [
      'Amyloid PET Scan',
      'Bone Scan w/NaF-18 (Skull Base to Mid Thigh)',
      'Bone Scan w/NaF-18 (Whole Body)',
      'Brain',
      'Brain Imaging Metabolic Evaluation',
      'CT Initial Staging',
      'Eyes - Thighs',
      'Myocardial PET Metabolic Evaluation',
      'Other',
      'Positron Emission Tomography (PET) Imaging; Skull Base to Mid-Thigh',
      'Positron Emission Tomography (PET) Imaging; Whole Body',
      'Tumor Imaging (Skull Base to Mid-Thigh)',
      'Tumor Imaging (Whole Body Melanoma/Sarcoma)',
    ]),
  },
  {
    key: 'pain_management',
    label: 'Pain Management',
    procedures: procs('Pain Management', [
      'Bone Marrow Aspirate Concentrate (BMAC)',
      'Bursa Injection',
      'Carpal Tunnel Injection',
      'Chronic Pelvic Pain Treatment',
      'Epidural Steroid Injection',
      'Facet Joint Injection',
      'Facet Nerve Injection',
      'Fine Needle Biopsy',
      'Muscle and Tendon Injections',
      'Nerve Root Block',
      'Nucleoplasty',
      'Radiofrequency Rhizotomy',
      'Sacroiliac (SI) Joint Injection',
      'Special Patient Needs',
      'Stellate Ganglion (Sympathetic) Block',
      'Steroid Injection',
      'Tenography',
      'Trigger Point Injection',
      'Vertebroplasty',
    ]),
  },
  {
    key: 'specials',
    label: 'Specials',
    procedures: procs('Specials', [
      'Arthrogram',
      'Bronchial Biopsy',
      'Bronchial Wash Bronchoalveolar Lavage',
      'DaTscan',
      'Diagnostic Bronchoscopy',
      'Discogram',
      'EEG',
      'Electromyography (EMG)',
      'Endoscopy',
      'Fistulagram',
      'Kyphoplasty',
      "Men's Mammogram",
      'Myelogram',
      'Nerve Conduction Study (NCS)',
      'Pediatric Imaging',
      'Pulmonary Function Test',
      'RapidArc IMRT',
      'Thyroid Biopsy',
      'Training for Intervention Procedures (TIPS)',
      'Transbronchial Biopsy',
      'Vena Cavagram',
    ]),
  },
  {
    key: 'ultrasound',
    label: 'Ultrasound',
    procedures: procs('Ultrasound', [
      'Abdomen',
      'Abdominal Aorta',
      'Abdominal Aortic',
      'Ankle-Brachial Index (ABI)',
      'Anus',
      'Aorta',
      'Appendix',
      'Ascites Localization',
      'Axilla',
      'Back Soft Tissue',
      'Bladder',
      'Breast',
      'Chest w/Mediastinum',
      'Endovaginal',
      'Extremities Non-Vascular',
      'Flow in/out Abdominal Organs',
      'Groin',
      'Hemodialysis',
      'Hernia',
      'Hips Infant',
      'Hysterosonogram',
      'Kidney Transplanted',
      'Liver',
      'Neonatal Head',
      'Neonatal Hips',
      'Obstetric',
      'Organ Doppler',
      'Other',
      'Pelvic',
      'Pleural Effusion Localization',
      'Posterior Knee',
      'Prostate',
      'Pylorus',
      'Rectum',
      'Renal',
      'Renal (with Bladder)',
      'Renal Allograft',
      'Renal Arteries',
      'Retroperitoneal Complete',
      'Scrotum/Testicles',
      'Soft Tissue Head/Neck',
      'Sonohysterogram',
      'Spinal Canal',
      'Testicular',
      'Thorax',
      'Thyroid',
      'TIPS Duplex',
      'TransRectal',
      'Transvaginal',
      'Uterus (Preg)',
    ]),
  },
  {
    key: 'vascular_ultrasound',
    label: 'Vascular Ultrasound',
    procedures: procs('Vascular Ultrasound', [
      'Carotid Doppler',
      'Hemodialysis Graft Study',
      'Lower Extremity Arterial (LEA)',
      'Other Vascular Ultrasound',
      'Sudomotor Autonomic',
      'Transcranial Doppler (TCD)',
      'Transthoracic Echocardiogram',
      'Upper Extremities Arterial (UEA)',
      'Vascular Abdominal Doppler',
      'Vein Mapping',
      'Venous Lower Extremity',
      'Venous Upper Extremity',
      'Videonystagmography (VNG)',
    ]),
  },
  {
    key: 'vein_treatment',
    label: 'Vein Treatment',
    procedures: procs('Vein Treatment', [
      'Critical Limb Ischemia Treatment',
      'Endovenous Therapy (Varicose)',
      'Peripheral Artery Disease Treatment',
      'Spider Vein Sclerotherapy',
      'Varicocele Treatment',
    ]),
  },
  {
    key: 'womens_health',
    label: "Women's Health",
    procedures: procs("Women's Health", [
      'Breast Cancer Risk Assessment',
      'Breast MRI',
      'Breast Needle Localization',
      'Breast Sonogram',
      'Breast Ultrasound',
      'Diagnostic Mammogram',
      'Diagnostic Mammography (3D Tomosynthesis)',
      'Ductography (Galactogram)',
      'High Risk Screening Mammogram',
      'Hysterosalpingography (HSG)',
      'Hysterosonogram',
      'MRI Guided Breast Biopsy',
      'Screening Mammogram',
      'Screening Mammography (3D Tomosynthesis)',
      'Stereotactic Breast Biopsy',
      'Super Order/Complete Breast Imaging Workup',
      'Transvaginal Ultrasound',
      'Ultrasound-Guided Breast Biopsy',
      'Uterine Fibroid Embolization',
    ]),
  },
  {
    key: 'xray',
    label: 'X-Ray',
    procedures: procs('X-Ray', [
      'Abdomen',
      'Ankle',
      'Bone Age',
      'Bone Age Wrists',
      'Bone Survey',
      'Calcaneus',
      'Cervical Spine',
      'Chest',
      'Clavicle',
      'Elbow',
      'Facial Bones',
      'Femur',
      'Fingers',
      'Foot',
      'Forearm',
      'Foreign Body Survey Child',
      'Hand',
      'Hip',
      'Hips',
      'Humerus',
      'Hysterosalpingography (HSG)',
      'Knee',
      'Lumbar Spine',
      'Lumbosacral Spine',
      'Mandible',
      'Mastoids',
      'Nasal Bones',
      'Neck, Soft Tissue',
      'Orbits',
      'Other',
      'PA and Lateral Chest',
      'Paranasal Sinuses',
      'Pelvis',
      'Ribs',
      'Sacroiliac Joints',
      'Sacroiliac Joints Less Than 3 Views (Trauma)',
      'Sacrum/Coccyx',
      'Scapula',
      'Scoliosis',
      'Shoulder',
      'Sinuses',
      'Skull',
      'Sternoclavicular Joints',
      'Sternum',
      'Temporomandibular Joints (TMJ)',
      'Thoracic Spine',
      'Thoracolumbar Spine',
      'Tibia-Fibula',
      'Toe',
      'Wrist',
    ]),
  },
]

export const RADIOLOGY_MODALITY_OPTIONS = RADIOLOGY_COMPENDIUM.map((m) => ({
  key: m.key,
  label: m.label,
}))
